// TelcoTrack — Unified building import: kanonická pole a mapování hlaviček ze všech zdrojů.
//
// Zdroje: docs/infrastructure/import_tables/ (preAkv, Export_ACQ, ROZV_GEN, CSV ROP, připojitelné).

import { normalizeAcquisitionText } from './acquisition-report.constants';
import { ACQUISITION_HEADER_ALIASES } from './acquisition-report.constants';
import type { TypCisla } from '../utils/typ-cisla.util';

/** Typ importního souboru / listu. */
export enum ImportSourceType {
  PRE_AKV = 'PRE_AKV',
  EXPORT_ACQ = 'EXPORT_ACQ',
  ROZV_GEN = 'ROZV_GEN',
  PRIPOJITELNY_ROP = 'PRIPOJITELNY_ROP',
  ROPID_CSV = 'ROPID_CSV',
}

/** Maximální vzdálenost (m) pro spatial matching BUDOVA v polygonu. */
export const BUILDING_IMPORT_SPATIAL_MAX_DISTANCE_M = 10;

/** Jeden řádek zdroje (hlavička → hodnota). */
export type BuildingImportRawRow = Record<string, string>;

/**
 * Kanonická pole budovy — jednotný formát pro všechny importní zdroje.
 * Rozšířená pole mimo sloupce tabulky `building_import_canonical` se ukládají do `data` JSONB.
 */
export interface CanonicalBuildingFields {
  kia?: string;
  ropId?: string;
  aqcId?: string;
  stobjId?: string;
  ruianId?: string;
  apCsId?: string;

  obec?: string;
  castObce?: string;
  ulice?: string;
  cisloPopisne?: string;
  /** Typ čísla domu (`P` = popisné, `E` = evidenční); chybí → neurčeno, DB doplní `P`. */
  typCisla?: TypCisla;
  orientacniA?: string;
  orientacniB?: string;
  psc?: string;
  parcelNumber?: string;
  katastralniUzemi?: string;
  celaAdresa?: string;

  /** S-JTSK easting (ST_X, EPSG:5514). */
  coordX?: number;
  /** S-JTSK northing (ST_Y, EPSG:5514). */
  coordY?: number;

  hpCount?: number;
  hpCountRop?: number;
  bjCount?: number;
  komerceCount?: number;
  rezervCount?: number;
  floorCount?: number;
  podzemniPodlazi?: number;

  statusAcq?: string;
  stavRop?: string;
  statusRopId?: string;
  souhlasSmlouva?: string;
  duvodNesouhlasu?: string;
  typDomu?: string;
  techReseniBd?: string;
  zpusobPripojeni?: string;

  optKabelVzdalenostM?: number;
  hdpeVzdalenostM?: number;
  trubSysVzdalenostM?: number;
  optickePripojky?: number;
  penetrace?: number;
  dslFlag?: boolean;
  upcFlag?: boolean;
  ostatniIspFlag?: boolean;

  rozvaecName?: string;
  fiberNumber?: string;

  ownerType?: string;
  ownerName?: string;
  ico?: string;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;

  kiaAssignedAt?: string;
}

export interface CanonicalMappedRow {
  fields: CanonicalBuildingFields;
  raw: Record<string, string>;
  rowIndex: number;
  sourceType: ImportSourceType;
}

/** Mapování Export_ACQ → kanonická pole (rozšíření stávajícího ACQUISITION_HEADER_ALIASES). */
export const EXPORT_ACQ_TO_CANONICAL: Partial<
  Record<keyof typeof ACQUISITION_HEADER_ALIASES, keyof CanonicalBuildingFields>
> = {
  aqcId: 'aqcId',
  statusAcq: 'statusAcq',
  souhlasSmlouva: 'souhlasSmlouva',
  duvodNesouhlasu: 'duvodNesouhlasu',
  hpCount: 'hpCount',
  bjCount: 'bjCount',
  komerceCount: 'komerceCount',
  rezervCount: 'rezervCount',
  floorCount: 'floorCount',
  typDomu: 'typDomu',
  techReseniBd: 'techReseniBd',
  zpusobPripojeni: 'zpusobPripojeni',
  parcelNumber: 'parcelNumber',
  katastralniUzemi: 'katastralniUzemi',
  cisloPopisne: 'cisloPopisne',
  cisloOrientacniA: 'orientacniA',
  ulice: 'ulice',
  mesto: 'obec',
  ropId: 'ropId',
  ownerType: 'ownerType',
  ownerName: 'ownerName',
  ico: 'ico',
  contactPerson: 'contactPerson',
  contactEmail: 'contactEmail',
  contactPhone: 'contactPhone',
};

/** preAkv (117/122 sloupců) — rozšířená akvizice. */
export const PREAKV_HEADER_ALIASES: Record<keyof CanonicalBuildingFields, string[]> = {
  aqcId: ['aqcid'],
  ropId: ['rop id'],
  stobjId: ['stobj id'],
  ruianId: ['ruian id'],
  apCsId: ['ap_cs id', 'ap cs id'],
  obec: ['obec', 'mesto'],
  castObce: ['cast obce'],
  ulice: ['ulice'],
  cisloPopisne: ['cislo popisne'],
  // Typ čísla domu není sloupec zdrojové tabulky — odvozuje se z textu adresy.
  typCisla: [],
  orientacniA: ['cislo orientacni a', 'cislo orientacni'],
  orientacniB: ['cislo orientacni b'],
  psc: ['psc'],
  parcelNumber: ['parcelni cislo'],
  katastralniUzemi: ['katastralni uzemi'],
  celaAdresa: ['cela adresa'],
  hpCount: ['pocet hp - zadani', 'pocet hp zadani', 'pocet hp'],
  hpCountRop: ['pocet hp na rop v ik0', 'pocet ik0 hp'],
  bjCount: ['pocet bj'],
  komerceCount: ['pocet komerce'],
  rezervCount: ['pocet rezerv'],
  floorCount: ['pocet podlazi vc. prizemi', 'pocet pater'],
  podzemniPodlazi: ['pocet podzemnich podlazi'],
  statusAcq: ['status acq'],
  stavRop: ['stav rop'],
  statusRopId: ['status rop id'],
  souhlasSmlouva: ['souhlas_smlouva', 'souhlas smlouva'],
  duvodNesouhlasu: ['duvod nesouhlasu/duvod vraceni akvizice', 'duvod nesouhlasu'],
  typDomu: ['typ domu', 'business objekty (typ)'],
  techReseniBd: [
    'zpusob projednavaneho technickeho reseni bd',
    'zpusob projednavaneho technickeho reseni',
  ],
  zpusobPripojeni: ['zpusob pripojeni objektu (bd/business)', 'zpusob pripojeni objektu'],
  ownerType: ['typ vlastnika/spravy', 'typ vlastnika'],
  ownerName: ['jmeno/prijmeni/nazev vlastnika/firmy', 'nazev vlastnika/firmy'],
  ico: ['ico'],
  contactPerson: ['kontaktni osoba (jmeno a prijmeni)', 'kontaktni osoba'],
  contactEmail: ['kontakt mail'],
  contactPhone: ['kontakt telefon'],
  kia: ['kia'],
  kiaAssignedAt: ['datum prirazeni kia k ropid'],
  coordX: ['souradnice x', 'x'],
  coordY: ['souradnice y', 'y'],
  optKabelVzdalenostM: [],
  hdpeVzdalenostM: [],
  trubSysVzdalenostM: [],
  optickePripojky: [],
  penetrace: [],
  dslFlag: [],
  upcFlag: [],
  ostatniIspFlag: [],
  rozvaecName: [],
  fiberNumber: [],
};

/** ROZV_GEN — rozvláknění + název rozvaděče. */
export const ROZV_GEN_HEADER_ALIASES: Record<keyof CanonicalBuildingFields, string[]> = {
  aqcId: ['aqcid'],
  ropId: ['rop id'],
  obec: ['mesto', 'obec'],
  ulice: ['ulice'],
  cisloPopisne: ['cislo popisne'],
  typCisla: [],
  orientacniA: ['cislo orientacni a', 'cislo orientacni'],
  parcelNumber: ['parcelni cislo'],
  katastralniUzemi: ['katastralni uzemi'],
  hpCount: ['pocet hp - zadani', 'pocet hp zadani', 'pocet hp'],
  bjCount: ['pocet bj'],
  komerceCount: ['pocet komerce'],
  statusAcq: ['status acq'],
  typDomu: ['typ domu'],
  techReseniBd: [
    'zpusob projednavaneho technickeho reseni bd',
    'zpusob projednavaneho technickeho reseni',
  ],
  zpusobPripojeni: ['zpusob pripojeni objektu (bd/business)', 'zpusob pripojeni objektu'],
  ownerName: ['jmeno/prijmeni/nazev vlastnika/firmy', 'nazev vlastnika/firmy'],
  contactPerson: ['kontaktni osoba (jmeno a prijmeni)', 'kontaktni osoba'],
  contactEmail: ['kontakt mail'],
  contactPhone: ['kontakt telefon'],
  rozvaecName: ['nazev rozvadecu', 'nazev rozvadece', 'nazev rozvaděče'],
  fiberNumber: ['fiber number', 'fibernumber'],
  stobjId: [],
  ruianId: [],
  apCsId: [],
  castObce: [],
  orientacniB: [],
  psc: [],
  celaAdresa: [],
  coordX: [],
  coordY: [],
  hpCountRop: [],
  rezervCount: [],
  floorCount: [],
  podzemniPodlazi: [],
  stavRop: [],
  statusRopId: [],
  souhlasSmlouva: [],
  duvodNesouhlasu: [],
  optKabelVzdalenostM: [],
  hdpeVzdalenostM: [],
  trubSysVzdalenostM: [],
  optickePripojky: [],
  penetrace: [],
  dslFlag: [],
  upcFlag: [],
  ostatniIspFlag: [],
  ownerType: [],
  ico: [],
  kia: [],
  kiaAssignedAt: [],
};

/** Seznam připojitelných objektů dle ROP (CSV). */
export const PRIPOJITELNY_OBJEKT_ALIASES: Record<keyof CanonicalBuildingFields, string[]> = {
  ropId: ['rop_id', 'rop id'],
  stobjId: ['id_stav_obj'],
  bjCount: ['sum_byt'],
  typDomu: ['typ_domu'],
  obec: ['obec'],
  ulice: ['ulice'],
  cisloPopisne: ['cp'],
  typCisla: [],
  orientacniA: ['coa'],
  orientacniB: ['cob'],
  celaAdresa: ['adresa'],
  optKabelVzdalenostM: ['opt_kabel_vzdalenost'],
  hdpeVzdalenostM: ['hdpe_vzdalenost'],
  trubSysVzdalenostM: ['trub_sys_vzdalenost'],
  penetrace: ['penetrace'],
  dslFlag: ['dsl'],
  upcFlag: ['upc'],
  ostatniIspFlag: ['ostatni_isp'],
  optickePripojky: ['opticke_pripojky'],
  coordX: ['x'],
  coordY: ['y'],
  aqcId: [],
  ruianId: [],
  apCsId: [],
  castObce: [],
  psc: [],
  parcelNumber: [],
  katastralniUzemi: [],
  hpCount: [],
  hpCountRop: [],
  komerceCount: [],
  rezervCount: [],
  floorCount: [],
  podzemniPodlazi: [],
  statusAcq: [],
  stavRop: [],
  statusRopId: [],
  souhlasSmlouva: [],
  duvodNesouhlasu: [],
  techReseniBd: [],
  zpusobPripojeni: [],
  rozvaecName: [],
  fiberNumber: [],
  ownerType: [],
  ownerName: [],
  ico: [],
  contactPerson: [],
  contactEmail: [],
  contactPhone: [],
  kia: [],
  kiaAssignedAt: [],
};

/** Kanonická pole, která se parsují jako integer. */
export const CANONICAL_INT_FIELDS = new Set<keyof CanonicalBuildingFields>([
  'hpCount',
  'hpCountRop',
  'bjCount',
  'komerceCount',
  'rezervCount',
  'floorCount',
  'podzemniPodlazi',
  'optKabelVzdalenostM',
  'hdpeVzdalenostM',
  'trubSysVzdalenostM',
  'optickePripojky',
  'penetrace',
]);

/** Kanonická pole, která se parsují jako boolean (0/1). */
export const CANONICAL_BOOL_FIELDS = new Set<keyof CanonicalBuildingFields>([
  'dslFlag',
  'upcFlag',
  'ostatniIspFlag',
]);

/** Kanonická pole souřadnic. */
export const CANONICAL_COORD_FIELDS = new Set<keyof CanonicalBuildingFields>(['coordX', 'coordY']);

export function isCanonicalFieldEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return !Number.isFinite(value);
  return false;
}

export function normalizeCanonicalHeader(value: string | null | undefined): string {
  return normalizeAcquisitionText(value);
}
